import { ILanguages, ITranslations } from '@llws/lift-entity-interfaces';
import { convertQueryCommandToQueryString, Equal, In, IQueryCommandOptions, IWhereCommand } from '@llws/dynamic-router-utils';
import { DataModifyProcessor } from '@/shared/classes/data-modify-processor';
import { adminRequest } from '@/shared/request-configs/admin-request';
import {
  IObjectWithTranslations,
} from '@/shared';
import config from '@/config';
import { translationFormatter } from '@/shared/helpers/translation-formatter';
import vm from '@/main';

const url = `${config.adminGateway}/translations-api/translations`;
/**
 * Fetches Translations by Client ID & Model <br>
 * API Endpoint - /translations-api/translations${query}` <br> via {@link adminRequest.get}
 * @param {number} clientId - id of the client
 * @param {string} model - model
 * @param {number[]} [foreignIds] - optional foreignIds array
 * @param {boolean} [useAppLanguage] - optional param to indicate the usage of other languages
 * @param {number[]} [siteClientIds] - optional array of clients to be used instead of a single one
 * @param {ILanguages} [useUniqueLanguage] - optional language object to be used instead of the app language
 */
export const getTranslationsByClientIdAndModel = async (
  clientId: number,
  model: string,
  foreignIds?: number[],
  useAppLanguage?: boolean,
  siteClientIds?: number[],
  useUniqueLanguage?: ILanguages,
): Promise<ITranslations[]> => {
  const extraFilters: IWhereCommand[] = [];

  if (useAppLanguage) {
    const app = vm();

    if (useUniqueLanguage) {
      extraFilters.push(Equal(
        'languageId',
        useUniqueLanguage.id,
      ));
    } else if (app && app.$rsGlobals.$language !== 'en') {
      extraFilters.push(Equal(
        'languageId',
        (app!.$rsGlobals.$siteSourceClientLanguages || []).find((l: ILanguages) => l.languageCode === app.$rsGlobals.$language)!.id,
      ));
    }
  }

  if (foreignIds) {
    extraFilters.push(In('foreignId', foreignIds));
  }
  const query: IQueryCommandOptions = {
    where: [
      siteClientIds ? In('clientId', [clientId, ...siteClientIds || []]) : Equal('clientId', clientId),
      Equal('model', model),
      ...extraFilters,
    ],
    limit: 99999,
  };
  const result = await adminRequest.get(`/translations-api/translations${convertQueryCommandToQueryString(query)}`);
  return result.data as ITranslations[];
};
/**
 * Saves Translations in Objects <br>
 * API Endpoint - {@link url} via {@link DataModifyProcessor}
 * @param {(IObjectWithTranslations<DataObject> | IObjectWithTranslations<DataObject>[])} data - data to modify
 * @param {string} model - model
 */
export const saveTranslationsInObjects = async <DataObject>(
  data: IObjectWithTranslations<DataObject> | IObjectWithTranslations<DataObject>[],
  model: string,
): Promise<IObjectWithTranslations<DataObject> | IObjectWithTranslations<DataObject>[]> => {
  const translations: ITranslations[] = [];
  const processor = new DataModifyProcessor<ITranslations>(url);
  let dataToModify = Array.isArray(data) ? data : [data];

  (dataToModify || []).forEach((d: any) => {
    (d?.languages || []).forEach((l: ILanguages) => {
      translations.push(...(l.translations!.map((t) => ({
        ...t,
        ...{ model, languageId: l.id!, foreignId: d.id },
      }))));
    });
  });

  (translations || []).forEach((t) => {
    if (!t.id && t.value) {
      processor.postItems.push(t);
    } else if (t.id) {
      processor.putItems.push(t);
    }
  });

  const result = await processor.execute();
  const trnsObjects = dataToModify[0] && dataToModify[0].languages?.length ? dataToModify[0].languages[0].translations || [] : [];
  const keys = Array.from(trnsObjects, (t: ITranslations) => t.key);
  const clientId = vm()?.$rsGlobals.$siteSourceClientId!;

  dataToModify = translationFormatter<any>(
    dataToModify,
    result.modifiedResultSet,
    dataToModify[0]?.languages || [],
    { model, keys, clientId },
  ) as any;

  return Array.isArray(data) ? dataToModify : dataToModify[0];
};
/**
 * Saves Translations <br>
 * API Endpoint - {@link url} via {@link DataModifyProcessor}
 * @param {ITranslations[]} data - translations to save
 */
export const saveTranslations = async (data: ITranslations[]) => {
  const processor = new DataModifyProcessor(url);
  processor.putItems = data.filter((t) => t.id);
  processor.postItems = data.filter((t) => !t.id);
  const result = await processor.execute();
  return result.modifiedResultSet;
};

export const getTranslationsByModelAndForeignIds = async (
  foreignIds: number[],
  model: string,
) => {
  const query: IQueryCommandOptions = {
    where: [
      Equal('model', model),
      In('foreignId', foreignIds),
    ],
    limit: 99999,
  };

  const result = await adminRequest.get<ITranslations[]>(
    `/translations-api/translations${convertQueryCommandToQueryString(query)}`,
  );

  return result.data!;
};

export const getTranslationsByClientIdModelKeyAndLanguageId = async (
  clientId: number,
  model: string,
  languageId: number,
  key: string,
) => {
  const query: IQueryCommandOptions = {
    where: [
      Equal('clientId', clientId),
      Equal('model', model),
      Equal('languageId', languageId),
      Equal('key', key),
    ],
    limit: 99999,
  };
  const result = await adminRequest.get(`/translations-api/translations${convertQueryCommandToQueryString(query)}`);
  return result.data as ITranslations[];
};

export default {
  getTranslationsByClientIdAndModel,
  saveTranslationsInObjects,
  saveTranslations,
  getTranslationsByModelAndForeignIds,
  getTranslationsByClientIdModelKeyAndLanguageId,
};
